/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik, FormikProvider, FieldArray} from 'formik'
import classNames from 'classnames'
import Dropdown from 'react-bootstrap/Dropdown'

import {KTSVG, TaxType, WitholdingType} from '../../../helpers'
import {
  KTCard,
  KTCardBody,
  InputPrice,
  Select,
  SelectCreatable,
  Loading,
  Datepicker,
} from '../../../components'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {
  allCompanyLookup,
  projectLookup,
  allProductLookup,
  basicLookup,
  staffGetAll,
} from '../../../redux/slices/lookup.slice'
import {proposalCreate} from '../../../redux/slices/proposal.slice'
import {productGetInfo, productCreateFast} from '../../../redux/slices/product.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs,
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  productLookup: state.lookup.productLookup,
  projectLookupList: state.lookup.projectLookupList,
  companyLookup: state.lookup.companyLookup,
  staffLookup: state.lookup.staffLookup,
  ProductUnit: state.lookup.ProductUnit,
  DiscountType: state.lookup.DiscountType,
  loading: state.lookup.loading,
})

const selectProposalData = (state) => ({
  isRedirect: state.proposal.isRedirect,
  loading: state.proposal.loading,
})

const selectProductData = (state) => ({
  detail: state.product.detail,
  isRedirect: state.product.isRedirect,
})

function Create() {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)

  const {
    productLookup,
    projectLookupList,
    companyLookup,
    staffLookup,
    ProductUnit,
    DiscountType,
    loading,
  } = useSelector(selectLookupData)
  const {isRedirect, loading: createLoading} = useSelector(selectProposalData)
  const {detail: product, isRedirect: createProduct} = useSelector(selectProductData)
  const dispatch = useDispatch()
  const [changeItem, setChangeItem] = useState()
  const [changeData, setChangeData] = useState()
  const [changeProduct, setChangeProduct] = useState()
  const [createItem, setCreateItem] = useState()
  const [visibiltyItem, setVisibiltyItem] = useState([
    {
      Discount: false,
      Description: false,
      WitholdingRate: false,
    },
  ])

  useEffect(() => {
    dispatch(allCompanyLookup())
    dispatch(projectLookup())
    dispatch(allProductLookup())
    dispatch(basicLookup('ProductUnit'))
    dispatch(basicLookup('DiscountType'))
    dispatch(staffGetAll())
  }, [dispatch])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    ProposalItems: Yup.array().of(
      Yup.object().shape({
        ProductId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        Amount: Yup.number()
          .typeError(intl.formatMessage({id: 'REQUIRED.VALID'}))
          .required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        UnitPrice: Yup.number()
          .typeError(intl.formatMessage({id: 'REQUIRED.VALID'}))
          .required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        ProductUnitId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        TaxId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      })
    ),
  }), [intl])

  const formValues = useMemoizedFormValues({
    Name: '',
    Terms: '',
    RelatedStaffId: '',
    ProjectId: null,
    CompanyId: null,
    EditDate: new Date(),
    DefermentDate: new Date(),
    SubTotalPrice: 0,
    TotalTax: 0,
    TotalDiscount: 0,
    GrossTotalPrice: 0,
    TotalWitholding: 0,
    TotalPrice: 0,
    ProposalItems: [
      {
        ProductId: null,
        Amount: 1,
        UnitPrice: 0,
        ProductUnitId: 1,
        TaxId: 6,
        TotalTax: null,
        SubTotalPrice: 0,
        TotalPrice: 0,
        Description: null,
        DiscountTypeId: 1,
        DiscountRate: 0,
        Discount: null,
        WitholdingRateId: null,
        Witholding: null,
      },
    ],
  })

  const handleSubmit = useCallback(async (values, {setSubmitting, resetForm}) => {
    setSubmitting(true)
    dispatch(proposalCreate(values))
    resetForm()
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.PROPOSAL'}),
      path: '/sales/proposal',
      isSeparator: false,
      isActive: false,
    },
  ], [intl])

  useEffect(() => {
    if (isRedirect && isRedirect.id) navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${isRedirect.id}`)
  }, [isRedirect, navigate, pageUrl])

  const onChangeItem = (target, value, index) => {
    setChangeItem({index, target})
    formik.setFieldValue(`ProposalItems[${index}][${target}]`, value)
  }

  const sumArrayItem = (item) => {
    return formik.values.ProposalItems.reduce((a, v) => (a = a + v[item]), 0)
  }

  useEffect(() => {
    const proposalItems = formik.values.ProposalItems[changeItem && changeItem.index]

    if (proposalItems) {
      const subTotalPrice = Number((proposalItems.UnitPrice * proposalItems.Amount).toFixed(4))
      const discount = Number((
        proposalItems.DiscountTypeId == 1
          ? (subTotalPrice * proposalItems.DiscountRate) / 100
          : proposalItems.DiscountRate
      ).toFixed(4))
      const tax = TaxType.find((item) => item.id == proposalItems.TaxId)
      const totalTax = Number((((subTotalPrice - discount) * (tax && tax.value || 0)) / 100).toFixed(4))
      const witholdingRate = WitholdingType.find(
        (item) => item.id == proposalItems.WitholdingRateId
      )
      const witholding = Number(((totalTax * (witholdingRate && witholdingRate.value || 0)) / 100).toFixed(4))
      const totalPrice = Number((subTotalPrice + totalTax - (witholding + discount)).toFixed(4))

      formik.setFieldValue(`ProposalItems[${changeItem?.index}].SubTotalPrice`, subTotalPrice)
      formik.setFieldValue(`ProposalItems[${changeItem?.index}].Discount`, discount)
      formik.setFieldValue(`ProposalItems[${changeItem?.index}].TotalTax`, totalTax)
      formik.setFieldValue(`ProposalItems[${changeItem?.index}].Witholding`, witholding)
      formik.setFieldValue(`ProposalItems[${changeItem?.index}].TotalPrice`, totalPrice)

      setChangeData(totalPrice)
    }
  }, [changeItem])

  useEffect(() => {
    formik.setFieldValue('SubTotalPrice', sumArrayItem('SubTotalPrice'))
    formik.setFieldValue('TotalTax', sumArrayItem('TotalTax'))
    formik.setFieldValue('TotalDiscount', sumArrayItem('Discount'))
    formik.setFieldValue(
      'GrossTotalPrice',
      sumArrayItem('SubTotalPrice') - sumArrayItem('Discount')
    )
    formik.setFieldValue('TotalWitholding', sumArrayItem('Witholding'))
    formik.setFieldValue('TotalPrice', sumArrayItem('TotalPrice'))
  }, [changeData])

  const onClickNewItem = (arrayHelpers) => {
    arrayHelpers.push({
      ProductId: null,
      Amount: 1,
      UnitPrice: 0,
      ProductUnitId: 1,
      TaxId: 6,
      TotalTax: null,
      SubTotalPrice: 0,
      TotalPrice: 0,
      Description: null,
      DiscountTypeId: 1,
      DiscountRate: 0,
      Discount: null,
      WitholdingRateId: null,
      Witholding: null,
    })
    setVisibiltyItem([
      ...visibiltyItem,
      {
        Discount: false,
        Description: false,
        WitholdingRate: false,
      },
    ])
  }

  const onClickVisibityItem = (item, index) => {
    const newArr = [...visibiltyItem]
    newArr[index][item] = !newArr[index][item]
    setVisibiltyItem(newArr)
  }

  const onChangeProduct = (item, index) => {
    formik.setFieldValue(`ProposalItems[${index}].ProductId`, Number(item?.value))
    setChangeProduct(index)
    dispatch(productGetInfo({Id: item?.value}))
  }

  useEffect(() => {
    if (product) {
      formik.setFieldValue(`ProposalItems[${changeProduct}].ProductUnitId`, product?.productUnitId)
      formik.setFieldValue(`ProposalItems[${changeProduct}].UnitPrice`, product?.baseSalePrice)
      formik.setFieldValue(`ProposalItems[${changeProduct}].TaxId`, product?.taxId)
      setChangeItem({index: changeProduct, target: 'UnitPrice'})
    }
  }, [product, changeProduct])

  const onClickFastProduct = (Name, Index) => {
    dispatch(productCreateFast({Name}))
    setCreateItem({Name, Index})
  }

  useEffect(() => {
    if (createProduct) dispatch(allProductLookup())
  }, [createProduct, dispatch])

  useEffect(() => {
    if (productLookup && createProduct) {
      formik.setFieldValue(`ProposalItems[${createItem?.Index}].ProductId`, createProduct.id)
    }
  }, [productLookup, createProduct, createItem?.Index])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>{intl.formatMessage({id: 'BC.PROPOSAL.ADD'})}</PageTitle>
      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-12'>
          <KTCard>
            <KTCardBody>
              <FormikProvider value={formik}>
                <form className='form' onSubmit={formik.handleSubmit} noValidate>
                  <div className='row mb-6'>
                    <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'PROPOSAL.CREATE.NAME'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <input
                        type='text'
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        required
                        disabled={formik.isSubmitting}
                        {...formik.getFieldProps('Name')}
                      />
                      {formik.touched.Name && formik.errors.Name && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.Name}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                      {intl.formatMessage({id: 'PROPOSAL.CREATE.PROJECTID'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Select
                        data={projectLookupList}
                        required
                        {...formik.getFieldProps('ProjectId')}
                        onChange={(item) => formik.setFieldValue('ProjectId', Number(item?.value))}
                      />
                      {formik.touched.ProjectId && formik.errors.ProjectId && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.ProjectId}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'PROPOSAL.CREATE.TERMS'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <input
                        type='text'
                        className='form-control form-control-solid mb-3 mb-lg-0'
                        autoComplete='off'
                        disabled={formik.isSubmitting}
                        {...formik.getFieldProps('Terms')}
                      />
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'PROPOSAL.CREATE.COMPANYID'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Select
                        data={companyLookup}
                        required
                        {...formik.getFieldProps('CompanyId')}
                        onChange={(item) => formik.setFieldValue('CompanyId', Number(item?.value))}
                      />
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'PROPOSAL.CREATE.RELATEDSTAFFID'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Select
                        data={staffLookup}
                        required
                        {...formik.getFieldProps('RelatedStaffId')}
                        onChange={(item) =>
                          formik.setFieldValue('RelatedStaffId', Number(item?.value))
                        }
                      />
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'PROPOSAL.CREATE.EDITDATE'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Datepicker
                        {...formik.getFieldProps('EditDate')}
                        onChange={([date]) => formik.setFieldValue('EditDate', date)}
                      />
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2'>
                      {intl.formatMessage({id: 'PROPOSAL.CREATE.DEFERMENTDATE'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Datepicker
                        {...formik.getFieldProps('DefermentDate')}
                        onChange={([date]) => formik.setFieldValue('DefermentDate', date)}
                      />
                    </div>
                  </div>
                  <FieldArray
                    name='ProposalItems'
                    render={(arrayHelpers) => (
                      <>
                        <div className='table-responsive'>
                          <table className='table g-5 gs-0 mb-0 fw-bold text-gray-700'>
                            <thead>
                              <tr className='border-bottom fs-7 fw-bold text-gray-700 text-uppercase'>
                                <th>
                                  {intl.formatMessage({
                                    id: 'PROPOSAL.CREATE.PROPOSALITEMS.PRODUCTID',
                                  })}
                                </th>
                                <th>
                                  {intl.formatMessage({id: 'PROPOSAL.CREATE.PROPOSALITEMS.AMOUNT'})}
                                </th>
                                <th>
                                  {intl.formatMessage({
                                    id: 'PROPOSAL.CREATE.PROPOSALITEMS.PRODUCTUNITID',
                                  })}
                                </th>
                                <th>
                                  {intl.formatMessage({
                                    id: 'PROPOSAL.CREATE.PROPOSALITEMS.UNITPRICE',
                                  })}
                                </th>
                                <th>
                                  {intl.formatMessage({id: 'PROPOSAL.CREATE.PROPOSALITEMS.TAXID'})}
                                </th>
                                <th>
                                  {intl.formatMessage({
                                    id: 'PROPOSAL.CREATE.PROPOSALITEMS.TOTALPRICE',
                                  })}
                                </th>
                                <th>&nbsp;</th>
                              </tr>
                            </thead>
                            <tbody>
                              {formik.values.ProposalItems.map((item, index) => (
                                <tr className='border-bottom border-bottom-dashed' key={index}>
                                  <td className='pe-7 ps-1'>
                                    <SelectCreatable
                                      data={productLookup}
                                      name={`ProposalItems[${index}].ProductId`}
                                      value={formik.values.ProposalItems[index].ProductId}
                                      required
                                      onChange={(item) => onChangeProduct(item, index)}
                                      onCreate={(item) => onClickFastProduct(item, index)}
                                    />

                                    {visibiltyItem[index].Description && (
                                      <input
                                        type='text'
                                        className='form-control form-control-solid mt-2'
                                        autoComplete='off'
                                        disabled={formik.isSubmitting}
                                        placeholder={intl.formatMessage({
                                          id: 'PROPOSAL.CREATE.PROPOSALITEMS.DESCRIPTION',
                                        })}
                                        name={`ProposalItems[${index}].Description`}
                                        value={formik.values.ProposalItems[index].Description}
                                        onChange={formik.handleChange}
                                      />
                                    )}
                                  </td>

                                  <td className='ps-0'>
                                    <input
                                      type='number'
                                      className='form-control form-control-solid'
                                      autoComplete='off'
                                      disabled={formik.isSubmitting}
                                      placeholder={intl.formatMessage({
                                        id: 'PROPOSAL.CREATE.PROPOSALITEMS.AMOUNT',
                                      })}
                                      name={`ProposalItems[${index}].Amount`}
                                      value={formik.values.ProposalItems[index].Amount}
                                      required
                                      onChange={(event) =>
                                        onChangeItem('Amount', Number(event.target.value), index)
                                      }
                                      onFocus={(event) => event.target.select()}
                                    />

                                    {visibiltyItem[index].Discount && (
                                      <div className='input-group mb-5'>
                                        <input
                                          type='number'
                                          className='form-control form-control-solid mt-2'
                                          autoComplete='off'
                                          disabled={formik.isSubmitting}
                                          placeholder={intl.formatMessage({
                                            id: 'PROPOSAL.CREATE.PROPOSALITEMS.DISCOUNTRATE',
                                          })}
                                          name={`ProposalItems[${index}].DiscountRate`}
                                          value={formik.values.ProposalItems[index].DiscountRate}
                                          onChange={(event) =>
                                            onChangeItem(
                                              'DiscountRate',
                                              Number(event.target.value),
                                              index
                                            )
                                          }
                                          onFocus={(event) => event.target.select()}
                                        />
                                        <Select
                                          className='mt-2'
                                          data={DiscountType}
                                          name={`ProposalItems[${index}].DiscountTypeId`}
                                          value={formik.values.ProposalItems[index].DiscountTypeId}
                                          required
                                          onChange={(item) =>
                                            onChangeItem(
                                              'DiscountTypeId',
                                              Number(item?.value),
                                              index
                                            )
                                          }
                                        />
                                      </div>
                                    )}
                                  </td>

                                  <td className='ps-0'>
                                    <Select
                                      data={ProductUnit}
                                      name={`ProposalItems[${index}].ProductUnitId`}
                                      value={formik.values.ProposalItems[index].ProductUnitId}
                                      required
                                      onChange={(item) =>
                                        onChangeItem('ProductUnitId', Number(item?.value), index)
                                      }
                                    />

                                    {visibiltyItem[index].WitholdingRate && (
                                      <Select
                                        data={WitholdingType}
                                        className={'mt-2'}
                                        name={`ProposalItems[${index}].WitholdingRateId`}
                                        value={formik.values.ProposalItems[index].WitholdingRateId}
                                        required
                                        onChange={(item) =>
                                          onChangeItem(
                                            'WitholdingRateId',
                                            Number(item?.value),
                                            index
                                          )
                                        }
                                      />
                                    )}
                                  </td>

                                  <td className='ps-0'>
                                    <InputPrice
                                      placeholder={intl.formatMessage({
                                        id: 'PROPOSAL.CREATE.PROPOSALITEMS.UNITPRICE',
                                      })}
                                      name={`ProposalItems[${index}].UnitPrice`}
                                      required
                                      value={formik.values.ProposalItems[index].UnitPrice}
                                      onChange={(value) =>
                                        onChangeItem('UnitPrice', Number(value), index)
                                      }
                                    />
                                  </td>

                                  <td className='ps-0'>
                                    <Select
                                      data={TaxType}
                                      name={`ProposalItems[${index}].TaxId`}
                                      value={formik.values.ProposalItems[index].TaxId}
                                      required
                                      onChange={(item) =>
                                        onChangeItem('TaxId', Number(item?.value), index)
                                      }
                                    />
                                  </td>

                                  <td className='ps-0'>
                                    <InputPrice
                                      placeholder={intl.formatMessage({
                                        id: 'PROPOSAL.CREATE.PROPOSALITEMS.TOTALPRICE',
                                      })}
                                      name={`ProposalItems[${index}].TotalPrice`}
                                      value={formik.values.ProposalItems[index].TotalPrice}
                                    />
                                  </td>

                                  <td className='pt-5 text-end'>
                                    <div className='d-flex'>
                                      <Dropdown autoClose='outside'>
                                        <Dropdown.Toggle
                                          variant='link'
                                          className='btn btn-sm btn-icon btn-active-color-info'
                                        >
                                          <KTSVG
                                            path='/images/icons/duotune/arrows/arr009.svg'
                                            className='svg-icon-2'
                                          />
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu>
                                          <Dropdown.Item
                                            onClick={() => onClickVisibityItem('Discount', index)}
                                            className={classNames({
                                              active: visibiltyItem[index].Discount,
                                            })}
                                          >
                                            {intl.formatMessage({
                                              id: 'PROPOSAL.CREATE.PROPOSALITEMS.DISCOUNT',
                                            })}
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            onClick={() =>
                                              onClickVisibityItem('Description', index)
                                            }
                                            className={classNames({
                                              active: visibiltyItem[index].Description,
                                            })}
                                          >
                                            {intl.formatMessage({
                                              id: 'PROPOSAL.CREATE.PROPOSALITEMS.DESCRIPTION',
                                            })}
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            onClick={() =>
                                              onClickVisibityItem('WitholdingRate', index)
                                            }
                                            className={classNames({
                                              active: visibiltyItem[index].WitholdingRate,
                                            })}
                                          >
                                            {intl.formatMessage({
                                              id: 'PROPOSAL.CREATE.PROPOSALITEMS.WITHOLDINGRATEID',
                                            })}
                                          </Dropdown.Item>
                                        </Dropdown.Menu>
                                      </Dropdown>
                                      <button
                                        type='button'
                                        className='btn btn-sm btn-icon btn-active-color-primary'
                                        onClick={() => arrayHelpers.remove(index)}
                                      >
                                        <KTSVG
                                          path='/images/icons/duotune/general/gen027.svg'
                                          className='svg-icon-2'
                                        />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className='border-top border-top-dashed align-middle fs-6 fw-bold text-gray-700'>
                                <th colSpan={formik.values.InvoiceTypeId === 1 ? 4 : 3}>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-outline btn-outline-dashed btn-outline-success btn-active-light-success'
                                    onClick={() => onClickNewItem(arrayHelpers)}
                                  >
                                    <KTSVG
                                      path='/images/icons/duotune/arrows/arr009.svg'
                                      className='svg-icon-2'
                                    />
                                    {intl.formatMessage({id: 'ADD_NEW_LINE'})}
                                  </button>
                                </th>
                                <th
                                  className='border-bottom border-bottom-dashed ps-0 fs-5'
                                  colSpan={2}
                                >
                                  {intl.formatMessage({id: 'PROPOSAL.CREATE.SUBTOTALPRICE'})}
                                </th>
                                <th className='border-bottom border-bottom-dashed text-end'>
                                  {intl.formatNumber(formik.values.SubTotalPrice, {
                                    style: 'currency',
                                    currency: 'try',
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 4,
                                  })}
                                </th>
                                <th>&nbsp;</th>
                              </tr>

                              {formik.values.TotalDiscount > 0 && (
                                <tr className='align-middle fw-bold text-gray-700'>
                                  <th colSpan={formik.values.InvoiceTypeId === 1 ? 4 : 3}></th>
                                  <th
                                    className='border-bottom border-bottom-dashed ps-0 fs-5'
                                    colSpan={2}
                                  >
                                    {intl.formatMessage({id: 'PROPOSAL.CREATE.TOTALDISCOUNT'})}
                                  </th>
                                  <th className='border-bottom border-bottom-dashed text-end'>
                                    {intl.formatNumber(formik.values.TotalDiscount, {
                                      style: 'currency',
                                      currency: 'try',
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 4,
                                    })}
                                  </th>
                                  <th>&nbsp;</th>
                                </tr>
                              )}

                              <tr className='align-middle fw-bold text-gray-700'>
                                <th colSpan={formik.values.InvoiceTypeId === 1 ? 4 : 3}></th>
                                <th
                                  className='border-bottom border-bottom-dashed ps-0 fs-5'
                                  colSpan={2}
                                >
                                  {intl.formatMessage({id: 'PROPOSAL.CREATE.GROSSTOTALPRICE'})}
                                </th>
                                <th className='border-bottom border-bottom-dashed text-end'>
                                  {intl.formatNumber(formik.values.GrossTotalPrice, {
                                    style: 'currency',
                                    currency: 'try',
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 4,
                                  })}
                                </th>
                                <th>&nbsp;</th>
                              </tr>

                              <tr className='align-middle fw-bold text-gray-700'>
                                <th colSpan={formik.values.InvoiceTypeId === 1 ? 4 : 3}></th>
                                <th
                                  className='border-bottom border-bottom-dashed ps-0 fs-5'
                                  colSpan={2}
                                >
                                  {intl.formatMessage({id: 'PROPOSAL.CREATE.TOTALTAX'})}
                                </th>
                                <th className='border-bottom border-bottom-dashed text-end'>
                                  {intl.formatNumber(formik.values.TotalTax, {
                                    style: 'currency',
                                    currency: 'try',
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 4,
                                  })}
                                </th>
                                <th>&nbsp;</th>
                              </tr>

                              {formik.values.TotalWitholding > 0 && (
                                <tr className='align-middle fw-bold text-gray-700'>
                                  <th colSpan={formik.values.InvoiceTypeId === 1 ? 4 : 3}></th>
                                  <th
                                    className='border-bottom border-bottom-dashed ps-0 fs-5'
                                    colSpan={2}
                                  >
                                    {intl.formatMessage({id: 'PROPOSAL.CREATE.TOTALWITHOLDING'})}
                                  </th>
                                  <th className='border-bottom border-bottom-dashed text-end'>
                                    {intl.formatNumber(formik.values.TotalWitholding, {
                                      style: 'currency',
                                      currency: 'try',
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 4,
                                    })}
                                  </th>
                                  <th>&nbsp;</th>
                                </tr>
                              )}

                              <tr className='align-middle fw-bold text-gray-700'>
                                <th colSpan={formik.values.InvoiceTypeId === 1 ? 4 : 3}></th>
                                <th className='fs-4 ps-0' colSpan={2}>
                                  {intl.formatMessage({id: 'PROPOSAL.CREATE.TOTALPRICE'})}
                                </th>
                                <th className='text-end fs-4 text-nowrap'>
                                  {intl.formatNumber(formik.values.TotalPrice, {
                                    style: 'currency',
                                    currency: 'try',
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 4,
                                  })}
                                </th>
                                <th>&nbsp;</th>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </>
                    )}
                  />

                  <div className='row pt-15'>
                    <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                      <button
                        className='btn btn-light'
                        onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}`)}
                      >
                        {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                      </button>
                      <button
                        type='submit'
                        className='btn btn-primary'
                        data-kt-users-modal-action='submit'
                        disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                      >
                        <span className='indicator-label'>
                          {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                        </span>
                        {formik.isSubmitting && (
                          <span className='indicator-progress'>
                            {intl.formatMessage({id: 'PLEASE'})}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </FormikProvider>
            </KTCardBody>
          </KTCard>
        </div>
      </div>

      {(loading | createLoading) > 0 && <Loading />}
    </>
  )
}
export {Create}
